const MINUTOS_POR_DIA = 24 * 60;

export const STATUS_DISPONIBILIDADE = Object.freeze({
  DISPONIVEL: {
    label: "Disponível",
    color: "green lighten-4",
    textColor: "green darken-4",
    indisponivel: false,
  },
  DISPONIVEL_OCUPADO: {
    label: "Disponível",
    color: "amber lighten-4",
    textColor: "amber darken-4",
    indisponivel: false,
  },
  INDISPONIVEL: {
    label: "Indisponível",
    color: "red lighten-4",
    textColor: "red darken-4",
    indisponivel: true,
  },
  INDISPONIVEL_OCUPADO: {
    label: "Indisponível",
    color: "amber lighten-4",
    textColor: "amber darken-4",
    indisponivel: true,
  },
});

export const ALERTAS_DISPONIBILIDADE = Object.freeze({
  TRABALHANDO_FORA_ESCALA: {
    label: "Fora da escala",
    icon: "mdi-briefcase-clock-outline",
    descricao: "Em atividade fora do horário da escala",
    color: "red",
    textColor: "white",
    geraHoraExtra: false,
  },
  ATIVIDADE_FORA_ESCALA: {
    label: "Fora da escala",
    icon: "mdi-calendar-alert",
    descricao: "A previsão da atividade ultrapassa o horário da escala",
    color: "red",
    textColor: "white",
    geraHoraExtra: true,
  },
  INTERVALO: {
    label: "Intervalo",
    icon: "mdi-food",
    descricao: "A previsão da atividade passa pelo intervalo da escala",
    color: "red",
    textColor: "white",
    geraHoraExtra: true,
  },
});

export function resolverDisponibilidade({
  emFerias = false,
  emEscala = false,
  ocupado = false,
  alertasAtividade = [],
}) {
  let chave;
  if (emFerias) chave = "INDISPONIVEL";
  else if (!emEscala) chave = ocupado ? "INDISPONIVEL_OCUPADO" : "INDISPONIVEL";
  else chave = ocupado ? "DISPONIVEL_OCUPADO" : "DISPONIVEL";

  const alertas = emFerias
    ? []
    : [
        ...(!emEscala && ocupado ? ["TRABALHANDO_FORA_ESCALA"] : []),
        ...alertasAtividade,
      ];

  return {
    chave,
    ...STATUS_DISPONIBILIDADE[chave],
    alertas: alertas.map((alerta) => ({
      chave: alerta,
      ...ALERTAS_DISPONIBILIDADE[alerta],
    })),
  };
}

export function obterPrioridadeOrdenacao(status, { emFerias = false } = {}) {
  if (emFerias) return 8;

  const alertas = (status?.alertas || []).map((alerta) => alerta.chave);
  const geraHoraExtra = (status?.alertas || []).some(
    (alerta) => alerta.geraHoraExtra,
  );

  switch (status?.chave) {
    case "DISPONIVEL":
      if (alertas.includes("ATIVIDADE_FORA_ESCALA")) return 3;
      if (alertas.includes("INTERVALO")) return 2;
      return 1;
    case "DISPONIVEL_OCUPADO":
      return geraHoraExtra ? 5 : 4;
    case "INDISPONIVEL_OCUPADO":
      return 6;
    default:
      return 7;
  }
}

function converterHorarioParaMinutos(horario) {
  if (!horario || typeof horario !== "string") return null;
  const [hora, minuto] = horario.split(":").map(Number);
  if (!Number.isFinite(hora) || !Number.isFinite(minuto)) return null;
  return hora * 60 + minuto;
}

function criarData(dia, minutos) {
  return new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), 0, minutos);
}

function dataValida(data) {
  return data instanceof Date && !isNaN(data.getTime());
}

export function montarTurnosEscala(escala, inicio, fim) {
  const turnos = [];
  const ultimoDia = criarData(fim, 0);

  for (
    let dia = criarData(inicio, -MINUTOS_POR_DIA);
    dia <= ultimoDia;
    dia = criarData(dia, MINUTOS_POR_DIA)
  ) {
    const diaSolides = dia.getDay() + 1;

    (escala || []).forEach((registro, indice) => {
      if (
        !registro ||
        registro.inativa === true ||
        Number(registro.dia) !== diaSolides
      ) {
        return;
      }

      const grupo = `${dia.getTime()}-${indice}`;
      [
        [registro.entrada1, registro.saida1],
        [registro.entrada2, registro.saida2],
      ].forEach(([entrada, saida]) => {
        const minutosEntrada = converterHorarioParaMinutos(entrada);
        const minutosSaida = converterHorarioParaMinutos(saida);
        if (
          minutosEntrada === null ||
          minutosSaida === null ||
          minutosEntrada === minutosSaida
        ) {
          return;
        }

        turnos.push({
          inicio: criarData(dia, minutosEntrada),
          fim: criarData(
            dia,
            minutosSaida < minutosEntrada
              ? minutosSaida + MINUTOS_POR_DIA
              : minutosSaida,
          ),
          grupo,
        });
      });
    });
  }

  return turnos.sort((a, b) => a.inicio - b.inicio);
}

export function avaliarAtividadeNaEscala(escala, inicio, fim) {
  if (!dataValida(inicio) || !dataValida(fim) || fim <= inicio) return [];

  const alertas = new Set();
  let cursor = inicio;
  let turnoAnterior = null;

  for (const turno of montarTurnosEscala(escala, inicio, fim)) {
    if (turno.fim <= cursor) {
      turnoAnterior = turno;
      continue;
    }

    if (turno.inicio > cursor) {
      alertas.add(
        turnoAnterior && turnoAnterior.grupo === turno.grupo
          ? "INTERVALO"
          : "ATIVIDADE_FORA_ESCALA",
      );
      if (turno.inicio >= fim) {
        cursor = fim;
        break;
      }
    }

    cursor = turno.fim;
    turnoAnterior = turno;
    if (cursor >= fim) break;
  }

  if (cursor < fim) alertas.add("ATIVIDADE_FORA_ESCALA");

  return Array.from(alertas);
}
